import { ARCInfo } from "arcjs/common/arc_devices";
import { AnyPlainObject } from "arcjs/common/misc";
import { DummyControlSource } from "arcjs/control_source/DummyControlSource";
import { ROSLibJSControlSource, ROSLibJSControlSourceOptions } from "arcjs/control_source/ROSLibJS";
import {
    WebROSProxyControlSource,
    WebROSProxyControlSourceArgs,
} from "arcjs/control_source/WebROSProxy";
import { FoxgloveControlSource } from "arcjs/control_source/Foxglove";
import { ControlSource } from "arcjs/control_source/index";
import { makeServiceCaller } from "arcjs/services/sources/index";
import { ServiceCaller } from "arcjs/services/base";
import { makeActionCaller } from "arcjs/action/sources/index";
import { ActionCaller } from "arcjs/action/base";
import { isHttpSecure } from "lib/utils/utils";
import { getAuthToken } from "lib/utils/auth";
import { gatewayServiceBaseUrl, gatewayWebSocketUrl } from "lib/consts/arcGateway";
import { isNumber } from "lodash";
import { Values } from "types/utilities";

type AddressOptions = {
    protocol?: string;
    host?: string;
    port?: string | number | null;
    subpath?: string | null;
};

type HTTPAddressOptions = Omit<AddressOptions, "protocol"> & {
    isSecure?: boolean;
};

export const getAddress = ({
    protocol = window.location.protocol,
    host = window.location.hostname,
    port = null,
    subpath = null,
}: AddressOptions = {}) => `${protocol}//${host}${port ? `:${port}` : ""}${subpath ? `/${subpath}` : ""}`;

export const getHTTPAddress = ({
    host = window.location.hostname,
    port = null,
    subpath = null,
    isSecure = isHttpSecure(),
}: HTTPAddressOptions = {}) => {
    return getAddress({ protocol: isSecure ? "https:" : "http:", host, port, subpath });
};

export const getWSAddress = ({
    host = window.location.hostname,
    port = null,
    subpath = null,
    isSecure = isHttpSecure(),
    token = undefined,
}: HTTPAddressOptions & { token?: string } = {}) => {
    const base = getAddress({ protocol: isSecure ? "wss:" : "ws:", host, port, subpath });
    return token ? `${base}?token=${encodeURIComponent(token)}` : base;
};

const webrosproxyOptionsFromEnv = () => {
    const options: WebROSProxyControlSourceArgs[1] = {};
    if (isNumber(process.env.REACT_APP_WEBROSPROXY_PORT_OVERRIDE)) {
        options.port = parseInt(process.env.REACT_APP_WEBROSPROXY_PORT_OVERRIDE);
    }
    return options;
};

export const getSocketByARCInfo = (info: ARCInfo) => {
    const options = info.controlSourceOptions
    const token = getAuthToken() ?? undefined;
    console.log('getSocketByARCInfo', info)
    switch (info.controlSource) {
        case "WEBROSPROXY":
            return new WebROSProxyControlSource(info.host, webrosproxyOptionsFromEnv());
        case "ROSLIBJS":
            return new ROSLibJSControlSource(info.host, {
                ...(options as ROSLibJSControlSourceOptions),
                token,
            });
        case "FOXGLOVE":
            return new FoxgloveControlSource(info.host, {
                ...(options as ROSLibJSControlSourceOptions),
                token,
            });
        case "DUMMY":
            return new DummyControlSource(info.serialNumber);
    }
};

type ControlSourceNames = Values<typeof ControlSource>["name"];

export const getSocketByControlSource = (host: string, controlSource: ControlSourceNames) => {
    const token = getAuthToken() ?? undefined;
    switch (controlSource) {
        case ControlSource.WEBROSPROXY.name:
            return new WebROSProxyControlSource(host, webrosproxyOptionsFromEnv());
        case ControlSource.ROSLIBJS.name:
            return new ROSLibJSControlSource(host, { token });
        default:
            return new DummyControlSource(host);
    }
};

export const getServiceCallerByARCInfo = (info: ARCInfo, controlSocket: any): ServiceCaller | null => {
    if (!controlSocket) return null;

    switch (info.controlSource) {
        case "WEBROSPROXY":
            // WebROSProxy uses HTTP endpoint for services
            const serviceUrl = getHTTPAddress({
                host: info.host,
                port: ControlSource.WEBROSPROXY.port,
                subpath: "service",
            });
            return makeServiceCaller("WEBROSPROXY", { serviceUrl });
        case "ROSLIBJS":
            // ROSLibJS uses the ROS socket for services
            return makeServiceCaller("ROSLIBJS", { ros: controlSocket.socket });
        case "FOXGLOVE":
            // Foxglove shares the same vendored Ros handle as the control source.
            return makeServiceCaller("FOXGLOVE", { ros: controlSocket.socket });
        case "DUMMY":
            return makeServiceCaller("DUMMY", []);
        default:
            return null;
    }
};

/**
 * Build the action caller for an ARC, sharing the SAME underlying `Ros`/socket as the
 * control source so topics, services and actions all ride one connection.
 * Only backends with a native action surface are wired: FOXGLOVE (sendGoal + monitorGoal)
 * and ROSLIBJS (sendGoal only). Others return null.
 */
export const getActionCallerByARCInfo = (info: ARCInfo, controlSocket: any): ActionCaller | null => {
    if (!controlSocket) return null;

    switch (info.controlSource) {
        case "FOXGLOVE":
            return makeActionCaller("FOXGLOVE", { ros: controlSocket.socket });
        case "ROSLIBJS":
            return makeActionCaller("ROSLIBJS", { ros: controlSocket.socket });
        default:
            return null;
    }
};

/**
 * Build the action caller from a control socket alone (no ARCInfo), by inspecting the
 * socket's concrete type. Shares the socket's `Ros` handle. Used by providers that hold
 * a control socket but not the ARCInfo. Returns null for backends without a native
 * action surface (WebROSProxy, Dummy).
 */
export const getActionCallerBySocket = (controlSocket: any): ActionCaller | null => {
    if (!controlSocket) return null;
    if (controlSocket instanceof FoxgloveControlSource) {
        return makeActionCaller("FOXGLOVE", { ros: controlSocket.socket });
    }
    if (controlSocket instanceof ROSLibJSControlSource) {
        return makeActionCaller("ROSLIBJS", { ros: controlSocket.socket });
    }
    return null;
};

/**
 * Join a path onto a base URL, PRESERVING any path already in the base.
 *
 * This used to do `u.pathname = path`, which overwrote the base's path
 * entirely. That was harmless while every base was a bare `host:port`, but the
 * gateway's bases carry a prefix (`http://<gateway-origin>/api/wifi`), and
 * overwriting would silently drop `/api/...` and produce a request the gateway
 * answers with the SPA's index.html instead of the API's JSON.
 *
 * Query strings and fragments on the base are preserved too — WebSocket token
 * queries and explicit operator parameters must survive path joining.
 */
export const joinUrl = (base: string, path: string): string => {
    const u = new URL(base);
    const basePath = u.pathname.replace(/\/$/, "");
    const suffix = path.startsWith("/") ? path : `/${path}`;
    u.pathname = `${basePath}${suffix}`;
    return u.toString();
};

/**
 * Janus signalling WebSocket URL. The path-routed fleet URL is a stable base;
 * Janus appends its session/handle paths below it, so no query propagation
 * wrapper is needed.
 */
export const getJanusServerAddress = (
    host: string,
    isSecure: boolean = isHttpSecure(),
    arcId?: string | null,
): string | null => {
    // Direct ARC mode: same-origin signalling through arc_gateway. The gateway
    // strips only /api, so Janus still sees its native /janus path and every
    // session suffix survives. WebRTC media does NOT traverse the gateway —
    // ICE/RTP stays peer-to-peer or TURN-relayed, exactly as before.
    const gateway = gatewayWebSocketUrl("janus", getAuthToken(), arcId, host);
    if (gateway) return gateway;

    // A fleet ARC without a proxy host is unavailable. Never fall back to a
    // direct/Tailscale address or to the legacy public port from the browser.
    if (arcId) return null;

    return getWSAddress({ host, port: "8188", isSecure, subpath: "janus", token: getAuthToken() ?? undefined });
};

/**
 * Rswebrtc signaling URL. It has no legacy port fallback: all browser traffic
 * goes through arc_gateway in direct mode or the selected ARC path in fleet.
 */
export const getWebRTCSignallingAddress = (
    host: string,
    arcId?: string | null,
): string | null => gatewayWebSocketUrl("webrtc", getAuthToken(), arcId, host);

/** Per-viewer ICE configuration route on the selected ARC. */
export const getWebRTCConfigAddress = (
    host: string,
    arcId?: string | null,
): string | null => gatewayServiceBaseUrl("webrtcConfig", arcId, host);

/** Janus Streaming mountpoint `description` in `janus.plugin.streaming.jcfg` (front mount). */
export const FRONT_CAMERA_STREAM_DESCRIPTION = "front";

/** True when the Janus stream description names a front camera (e.g. front, Front, front cam, frontcam). */
export const isFrontCameraStream = (streamDescription: string | null | undefined): boolean =>
    typeof streamDescription === "string" &&
    streamDescription.toLowerCase().includes("front");

/** True when the Janus stream description names the thermal camera. */
export const isThermalCameraStream = (streamDescription: string | null | undefined): boolean =>
    typeof streamDescription === "string" &&
    streamDescription.toLowerCase().includes("thermal");

/** True when the Janus stream description names a gimbal camera. */
export const isGimbalCameraStream = (streamDescription: string | null | undefined): boolean =>
    typeof streamDescription === "string" &&
    streamDescription.toLowerCase().includes("gimbal");

/**
 * True when the stream is the arm-mounted RealSense (D405).
 * Matches producer keys `cam` / `arm_cam` and labels like "Arm Cam" / "Arm D405".
 */
export const isArmCameraStream = (streamDescription: string | null | undefined): boolean => {
    if (typeof streamDescription !== "string") return false;
    const normalized = streamDescription.toLowerCase().replace(/[_-]+/g, " ").trim();
    if (normalized === "cam" || normalized === "arm cam" || normalized === "armcam") {
        return true;
    }
    return normalized.includes("arm cam") || normalized.includes("arm d405");
};

/** Label for the camera source picker / pinned popup title. */
export const streamDisplayLabel = (
    streamKey: string | null | undefined,
    catalogDisplayName?: string | null,
): string => {
    const key = typeof streamKey === "string" ? streamKey.trim() : "";
    const fromCatalog = typeof catalogDisplayName === "string" ? catalogDisplayName.trim() : "";
    if (fromCatalog && fromCatalog.toLowerCase() !== "cam") {
        // Older overlays seeded "Arm D405"; prefer the product label.
        if (fromCatalog === "Arm D405") return "Arm Cam";
        return fromCatalog;
    }
    if (isArmCameraStream(key) || isArmCameraStream(fromCatalog)) return "Arm Cam";
    return fromCatalog || key;
};

/** True when the active Janus stream matches the selected inference source. */
export const streamMatchesInferenceSource = (
    streamDescription: string | null | undefined,
    sourceId: string | null | undefined,
): boolean =>
    sourceId === "gimbal"
        ? isGimbalCameraStream(streamDescription)
        : isFrontCameraStream(streamDescription);

/** Prefer front camera when listed; otherwise first available stream (Janus sort order). */
export const pickDefaultStreamingSource = (streamDescriptions: string[]): string | null => {
    if (streamDescriptions.length === 0) return null;
    return streamDescriptions.find(isFrontCameraStream) ?? streamDescriptions[0];
};

const getBboxHttpPort = (): string =>
    typeof process.env.REACT_APP_BBOX_HTTP_PORT === "string" &&
        process.env.REACT_APP_BBOX_HTTP_PORT.length > 0
        ? process.env.REACT_APP_BBOX_HTTP_PORT
        : "8030";

/** rtsp_inferencing_module HTTP base (detections, recognition) on the ARC host. */
export const getBboxServiceUrl = (host: string, subpath: string, isSecure: boolean = isHttpSecure(), arcId?: string | null) => {
    const gateway = gatewayServiceBaseUrl("bbox", arcId, host);
    if (gateway) return `${gateway}/${subpath.replace(/^\//, "")}`;
    if (arcId) return null;
    return getHTTPAddress({ host, port: getBboxHttpPort(), subpath, isSecure });
};

/** Poll bbox overlay JSON from rtsp_inferencing_module (same RTSP as front mount). */
export const getBboxDetectionsUrl = (host: string, isSecure: boolean = isHttpSecure(), arcId?: string | null) =>
    getBboxServiceUrl(host, "detections", isSecure, arcId);

const getNanoowlHttpPort = (): string =>
    typeof process.env.REACT_APP_NANOOWL_HTTP_PORT === "string" &&
        process.env.REACT_APP_NANOOWL_HTTP_PORT.length > 0
        ? process.env.REACT_APP_NANOOWL_HTTP_PORT
        : "12401";

/** NanoOWL HTTP API on the ARC host (/detections, /prompts). */
export const getNanoowlServiceUrl = (host: string, subpath: string, isSecure: boolean = isHttpSecure(), arcId?: string | null) => {
    const gateway = gatewayServiceBaseUrl("nanoowl", arcId, host);
    if (gateway) return `${gateway}/${subpath.replace(/^\//, "")}`;
    if (arcId) return null;
    return getHTTPAddress({ host, port: getNanoowlHttpPort(), subpath, isSecure });
};

export const getNanoowlDetectionsUrl = (host: string, isSecure: boolean = isHttpSecure(), arcId?: string | null) =>
    getNanoowlServiceUrl(host, "detections", isSecure, arcId);

const getThermalHttpPort = (): string =>
    typeof process.env.REACT_APP_THERMAL_HTTP_PORT === "string" &&
        process.env.REACT_APP_THERMAL_HTTP_PORT.length > 0
        ? process.env.REACT_APP_THERMAL_HTTP_PORT
        : "12420";

/** thermal_detection HTTP API on the ARC host (/detections, /threshold, /recognition). */
export const getThermalServiceUrl = (
    host: string,
    subpath: string,
    isSecure: boolean = isHttpSecure(),
    arcId?: string | null
) => {
    const gateway = gatewayServiceBaseUrl("thermal", arcId, host);
    if (gateway) return `${gateway}/${subpath.replace(/^\//, "")}`;
    if (arcId) return null;
    return getHTTPAddress({ host, port: getThermalHttpPort(), subpath, isSecure });
};

export const getThermalDetectionsUrl = (
    host: string,
    isSecure: boolean = isHttpSecure(),
    arcId?: string | null
) => getThermalServiceUrl(host, "detections", isSecure, arcId);
